import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

type UploadCompanyLogoArguments = {
  companyId: UUID;
  logoImage: Blob;
};

/**
 * Updates a company logo.
 *
 * @param {string} companyId - The ID of the company for which to update the logo.
 * @param {Object} logoImage - A Blob representing the logo image.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/upload_company_logo_company__id__logo_put
 */
export const uploadCompanyLogo = async (companyId: UUID, logoImage: Blob) => {
  const formData = new FormData();
  formData.append('file', logoImage);

  const response = await vestigasApi
    .put(ENDPOINT.COMPANY.UPLOAD_LOGO(companyId), {
      body: formData,
      headers: {
        'Content-Type': logoImage.type,
      },
    })
    .json();

  return response;
};

/**
 * React Query mutation hook for uploading a company logo.
 *
 * This hook provides functionality to:
 * - Upload a new logo image for a company
 * - Automatically invalidate the company query cache on completion
 *
 * @returns {UseMutationResult} A mutation object containing the upload function and status
 *
 * @example
 * const mutation = useMutationCompanyLogoUpload(companyId);
 * mutation.mutate(logoImageBlob);
 */
export const useMutationCompanyLogoUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, logoImage }: UploadCompanyLogoArguments) =>
      uploadCompanyLogo(companyId, logoImage),
    onSettled(_, __, { companyId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.get(companyId),
      });
    },
    onSuccess() {
      // ... show toast or something
    },
  });
};
